/**
 * Permission descriptors used both backend and frontend.
 *
 * A permission is a small object with up to three checks:
 *   - `key`     — looked up in the user's resolved permission list (role + direct grants)
 *   - `gate`    — pure function `(user) => boolean`, evaluated at AuthGuard time
 *   - `filter`  — pure function `(user, resource) => boolean`, evaluated at the
 *                 service layer once the resource is loaded (ABAC)
 *
 * All three are optional. Catalogued permissions (declared in `*.permission.ts`)
 * always have a `key`. Anonymous permissions — constructed inline in a
 * controller, e.g. `Permission({ gate: u => u.email?.endsWith('@…') })` — have
 * no key and are intentionally absent from the catalogue.
 *
 * Permissions live under `<module>/permissions/`. Each file exports one named
 * value: a single `Permission(...)`, a `Permissions({...})` group, or a
 * `crud(...)` bundle. The auth module's `permission.discovery.ts` finds them,
 * flattens groups, and registers every catalogued (keyed) Permission as a DI
 * provider; `PermissionCatalogueService` indexes them via NestJS DiscoveryService.
 */

export interface User {
    readonly id?: string;
    readonly email?: string;
    readonly name?: string | null;
    readonly permissions: readonly string[];
}

export type PermissionGate = (user: User) => boolean | Promise<boolean>;
export type PermissionFilter<R> = (user: User, resource: R) => boolean | Promise<boolean>;

export interface Permission<R = unknown> {
    /** Stable identifier looked up in the user's resolved permission list. Omit for anonymous permissions. */
    readonly key?: string;
    /** Human-friendly label for the admin UI. Omit for anonymous permissions. */
    readonly label?: string;
    /** Group in the admin UI (e.g. "Inventory"). Omit for anonymous permissions. */
    readonly category?: string;
    readonly description?: string;
    /**
     * Guard-time gate, evaluated by AuthGuard with no resource. Use this for
     * cross-cutting concerns the guard can decide locally — e.g. an email-domain
     * allowlist. Runs alongside the `key` check (both must pass).
     */
    readonly gate?: PermissionGate;
    /**
     * Resource-level ABAC filter, evaluated at the service layer with the loaded
     * resource. The AuthGuard does NOT evaluate this — controllers/services are
     * expected to call `can(permission, user, resource)` after loading the entity.
     */
    readonly filter?: PermissionFilter<R>;
}

const MARKER: unique symbol = Symbol.for('balancia.permission');

interface PermissionInternal<R> extends Permission<R> {
    readonly [MARKER]: true;
}

/**
 * Wrap a plain descriptor as a Permission. The returned object is frozen and
 * carries an internal marker symbol so {@link isPermission} can recognize it
 * after auto-discovery.
 */
export const Permission = <R = unknown>(spec: Permission<R>): Permission<R> => {
    const tagged = { ...spec, [MARKER]: true as const };
    return Object.freeze(tagged) as PermissionInternal<R>;
};

export const isPermission = (value: unknown): value is Permission => {
    return (
        typeof value === 'object' &&
        value !== null &&
        (value as Record<symbol, unknown>)[MARKER] === true
    );
};

/** Token used to inject a discovered permission via DI (one provider per key). */
export const PERMISSION_TOKEN = (key: string): string => `PERMISSION:${key}`;

/* ---------------- groups + crud helper ---------------- */

const GROUP_MARKER: unique symbol = Symbol.for('balancia.permission-group');

/**
 * Anything a `Permissions({...})` group may hold — a leaf {@link Permission} or
 * a nested record of more members. Used by the discovery flattener and as the
 * constraint on `Permissions`. Callers should rarely have to type this directly:
 * `Permissions` and `crud` preserve their input shape so accessors like
 * `InventoryPermissions.product.delete` resolve to `Permission` directly.
 */
// `any` for the resource type: a group may mix permissions over different
// resource shapes (e.g. an inventory bundle has read/write/delete with no
// resource alongside a delete with an Inventory resource for ABAC). Variance
// over the resource generic would otherwise reject those.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PermissionMember = Permission<any> | { [key: string]: PermissionMember };

/**
 * Bundle multiple permissions (and sub-groups) into one named value. The
 * discovery walker flattens groups recursively and registers every leaf
 * Permission as a DI provider, so you can colocate a whole module's permission
 * surface in a single file without exploding the file count.
 *
 *   export const InventoryPermissions = Permissions({
 *       inventory:    crud('inventory',      'inventories',     'Inventory'),
 *       type:         crud('inventory-type', 'inventory types', 'Inventory'),
 *       transaction:  crud('transaction',    'transactions',    'Inventory', { delete: false }),
 *   });
 */
export const Permissions = <T extends Record<string, PermissionMember>>(members: T): T => {
    Object.defineProperty(members, GROUP_MARKER, { value: true, enumerable: false });
    return Object.freeze(members);
};

export const isPermissionGroup = (value: unknown): value is Record<string, PermissionMember> => {
    return (
        typeof value === 'object' &&
        value !== null &&
        (value as Record<symbol, unknown>)[GROUP_MARKER] === true
    );
};

/** Walk a permission, group, or arbitrary nested structure and yield each leaf Permission. */
export const flattenPermissions = (value: PermissionMember): Permission[] => {
    if (isPermission(value)) return [value];
    if (isPermissionGroup(value)) {
        const out: Permission[] = [];
        for (const child of Object.values(value)) out.push(...flattenPermissions(child));
        return out;
    }
    return [];
};

export type CrudAction = 'read' | 'write' | 'delete';

/**
 * Per-action override. `false` removes the action entirely; an object overrides
 * any subset of the permission's fields (commonly `label`, `description`,
 * `gate`, or `filter`).
 */
export type CrudOverride<R = unknown> = Partial<{
    [A in CrudAction]: false | Partial<Omit<Permission<R>, 'key'>>;
}>;

const CRUD_LABEL: Record<CrudAction, (plural: string) => string> = {
    read:   (plural) => `View ${plural}`,
    write:  (plural) => `Create/update ${plural}`,
    delete: (plural) => `Delete ${plural}`,
};

/**
 * CRUD bundle factory. Generates `read`/`write`/`delete` permissions under one
 * key namespace (`<single>:<action>`) with auto-derived labels using `plural`.
 * Each action can be customised — or removed by setting it to `false` — via the
 * `override` map. The result is a discoverable group (Permissions-wrapped at
 * runtime) and its keys narrow to `Permission`.
 *
 *   crud('inventory', 'inventories', 'Inventory', {
 *       delete: { filter: (u, inv: Inventory) => inv.ownerId === u.id },
 *   })
 *   // → { read, write, delete }   each typed as Permission
 *
 *   crud('tare', 'tare pairings', 'Tare', { delete: false })
 *   // → { read, write }            no delete in the type
 */
export const crud = <R = unknown, O extends CrudOverride<R> = {}>(
    single: string,
    plural: string,
    name: string,
    override?: O,
): { [K in CrudAction as O[K] extends false ? never : K]: Permission<R> } => {
    const out: Record<string, Permission<R>> = {};
    for (const action of ['read', 'write', 'delete'] as const) {
        const o = override?.[action];
        if (o === false) continue;
        out[action] = Permission<R>({
            key: `${single}:${action}`,
            label: o?.label ?? CRUD_LABEL[action](plural),
            category: o?.category ?? name,
            description: o?.description,
            gate: o?.gate,
            filter: o?.filter,
        });
    }
    return Permissions(out) as never;
};

/* ---------------- key matching (shared between backend & frontend) ---------------- */

/** Does the user's key list cover `required` (exact, `*` wildcard, or `prefix:*`)? */
export const matchesKey = (userPermissions: readonly string[], required: string): boolean => {
    for (const owned of userPermissions) {
        if (owned === required) return true;
        if (owned === '*') return true;
        if (owned.endsWith(':*')) {
            const prefix = owned.slice(0, -1);
            if (required.startsWith(prefix)) return true;
        }
    }
    return false;
};

/* ---------------- evaluation helpers ---------------- */

/**
 * Full permission check.
 *
 *   - `key`    must be present in `user.permissions` (or covered by a wildcard).
 *   - `gate`   if defined, must return truthy for `user`.
 *   - `filter` if defined AND `resource` is provided, must return truthy for `(user, resource)`.
 *
 * If `filter` is defined but `resource` is omitted, the filter is NOT evaluated:
 * AuthGuard relies on this to skip ABAC at guard time, deferring to the service
 * layer to re-check with the loaded resource.
 */
export const can = async <R>(permission: Permission<R>, user: User, resource?: R): Promise<boolean> => {
    if (permission.key !== undefined && !matchesKey(user.permissions, permission.key)) return false;
    if (permission.gate && !(await permission.gate(user))) return false;
    if (permission.filter && resource !== undefined) {
        if (!(await permission.filter(user, resource))) return false;
    }
    return true;
};

/** Sync variant — safe when all evaluated callbacks are sync. Throws if any returns a promise. */
export const canSync = <R>(permission: Permission<R>, user: User, resource?: R): boolean => {
    const checkSync = (result: boolean | Promise<boolean>, what: string): boolean => {
        if (result && typeof (result as Promise<boolean>).then === 'function') {
            throw new Error(`Permission "${permission.key ?? '<anonymous>'}" has an async ${what}; use can() instead of canSync()`);
        }
        return Boolean(result);
    };
    if (permission.key !== undefined && !matchesKey(user.permissions, permission.key)) return false;
    if (permission.gate && !checkSync(permission.gate(user), 'gate')) return false;
    if (permission.filter && resource !== undefined) {
        if (!checkSync(permission.filter(user, resource), 'filter')) return false;
    }
    return true;
};

/* ---------------- composite helpers ---------------- */

/**
 * OR-combine permissions: passes if any member would pass. The result is an
 * anonymous Permission (no key) intentionally absent from the catalogue.
 *
 *   permissions: or([
 *       AdminPermissions.users,
 *       Permission({ gate: (u) => u.email?.endsWith('@station-ssca.com') ?? false }),
 *   ])
 *
 * The composite's `gate` runs at AuthGuard time: a member with only an ABAC
 * `filter` (no key, no gate) is treated as "may pass" so the request is allowed
 * through to the service layer, which re-checks with the loaded resource.
 */
export const or = (members: Permission[], label?: string): Permission => {
    const memberPassesAtGuard = async (m: Permission, user: User): Promise<boolean> => {
        if (m.key !== undefined && !matchesKey(user.permissions, m.key)) return false;
        if (m.gate && !(await m.gate(user))) return false;
        // ABAC filter (if any) is deferred to the service layer — at guard time,
        // a member that's otherwise eligible is treated as a pass.
        return true;
    };
    return Permission({
        label,
        gate: async (user) => {
            for (const m of members) {
                if (await memberPassesAtGuard(m, user)) return true;
            }
            return false;
        },
        filter: async (user, resource) => {
            for (const m of members) {
                if (await can(m, user, resource)) return true;
            }
            return false;
        },
    });
};

/* ---------------- legacy frontend helpers ---------------- */

/** Frontend gates accept a single Permission or an array (AND across the array). */
export type PermissionRequirement = Permission | Permission[];

/** True when every requirement in `required` is satisfied by `user.permissions`. */
export const hasPermission = (user: User, required: PermissionRequirement): boolean => {
    const list = Array.isArray(required) ? required : [required];
    return list.every((p) => canSync(p, user));
};
